import type {
  UtilityBillsResponse,
  AddApartmentRequest,
  ApartmentPaymentsResponse,
  AddApartmentContactRequest,
  AddApartmentDocumentRequest,
  AddApartmentPaymentRequest,
  ApartmentDocumentResponse,
  ApartmentResponse,
  ApartmentsResponse,
  UpdateApartmentRequest,
  AddDocumentRequest,
  ArchiveProvider,
  EmailCheckResponse,
  EmailSettingsResponse,
  SmtpSettingsInput,
  DocumentResponse,
  DocumentsResponse,
  HealthDocumentKind,
  AddTranscriptRequest,
  ApiError as ApiErrorBody,
  RecordingsResponse,
  TranscribeRecordingRequest,
  TranscriptionJobsResponse,
  AuditResponse,
  SetGrantRequest,
  AddUserRequest,
  SetDisabledRequest,
  SetRoleRequest,
  TranscriptKind,
  UserDto,
  TranscriptResponse,
  TranscriptsResponse,
  UsersResponse,
  BookingsResponse,
  CalendarsResponse,
  ChangePasswordRequest,
  ConnectionsResponse,
  CreateSessionResponse,
  DigestSettings,
  SchedulePreviewResponse,
  ScheduleResponse,
  EventPage,
  GoogleStatus,
  LabReportResponse,
  LabReportsResponse,
  MeResponse,
  MeetingsResponse,
  PagesResponse,
  StatusResponse,
  TelegramAccountPatch,
  TelegramAccountsResponse,
  TelegramAccountView,
  TelegramChatAdd,
  TelegramChatPatch,
  TelegramChatsResponse,
  TelegramGroupsResponse,
  TelegramChatView,
  TelegramLoginResponse,
  TelegramLoginStart,
  WhatsAppAccountPatch,
  WhatsAppAccountsResponse,
  WhatsAppAccountView,
  WhatsAppLinkResponse,
  WhatsAppLinkStart,
  WhoopStatus,
} from '../../../shared/api.js';

const TOKEN_KEY = 'cybernika.token';
export const AUTH_REQUIRED_EVENT = 'cybernika:auth-required';

export function getToken(): string | undefined {
  try {
    return localStorage.getItem(TOKEN_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

export function setToken(token: string | undefined): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* storage unavailable: the token lives only for this page */
  }
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

function headers(extra?: HeadersInit): Headers {
  const result = new Headers(extra);
  const token = getToken();
  if (token) result.set('Authorization', `Bearer ${token}`);
  return result;
}

async function toApiError(response: Response): Promise<ApiError> {
  let code = `http_${response.status}`;
  let message = response.statusText || 'Request failed';
  try {
    const body = (await response.json()) as Partial<ApiErrorBody>;
    if (body.code) code = body.code;
    if (body.message) message = body.message;
  } catch {
    /* not JSON */
  }
  if (response.status === 401) window.dispatchEvent(new Event(AUTH_REQUIRED_EVENT));
  return new ApiError(response.status, code, message);
}

export async function request(path: string, init: RequestInit = {}): Promise<Response> {
  const response = await fetch(`/api${path}`, { ...init, headers: headers(init.headers) });
  if (!response.ok) throw await toApiError(response);
  return response;
}

async function json<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await request(path, init);
  return (await response.json()) as T;
}

function patchJson(path: string, body: unknown): Promise<Response> {
  return request(path, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

function postJson(path: string, body: unknown): Promise<Response> {
  return request(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export const api = {
  me: () => json<MeResponse>('/me'),
  status: () => json<StatusResponse>('/status'),
  connections: () => json<ConnectionsResponse>('/connections'),
  whoopCheck: () => json<WhoopStatus>('/connections/whoop/check', { method: 'POST' }),
  whoopDisconnect: () =>
    json<{ revoked: boolean }>('/connections/whoop/disconnect', { method: 'POST' }),
  googleCheck: () => json<GoogleStatus>('/connections/google/check', { method: 'POST' }),
  googleUnlink: () => json<{ revoked: boolean }>('/connections/google/unlink', { method: 'POST' }),
  audit: (limit = 100) => json<AuditResponse>(`/audit?limit=${limit}`),
  calendars: (refresh = false) =>
    json<CalendarsResponse>(`/calendars${refresh ? '?refresh=1' : ''}`),
  meetings: () => json<MeetingsResponse>('/meetings'),
  calendarEvents: (params: { start?: string; end?: string; limit?: number } = {}) => {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) query.set(key, String(value));
    }
    const suffix = query.size > 0 ? `?${query.toString()}` : '';
    return json<EventPage>(`/events${suffix}`);
  },
  labReports: () => json<LabReportsResponse>('/labs/reports'),
  labReport: (id: string) => json<LabReportResponse>(`/labs/reports/${encodeURIComponent(id)}`),
  changePassword: (body: ChangePasswordRequest) => postJson('/me/password', body),
  pages: () => json<PagesResponse>('/pages'),
  bookings: (all = false) => json<BookingsResponse>(`/bookings${all ? '?all=1' : ''}`),
  closeBooking: (id: number) => request(`/bookings/${id}/close`, { method: 'POST' }),
  retryBookingEvent: (key: string) =>
    json<{ status: string; error?: string }>(`/bookings/invites/${encodeURIComponent(key)}/retry`, {
      method: 'POST',
    }),
  deletePage: (key: string) => request(`/pages/${encodeURIComponent(key)}`, { method: 'DELETE' }),

  transcripts: (params: { kind?: TranscriptKind; query?: string; limit?: number } = {}) => {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== '') query.set(key, String(value));
    }
    const suffix = query.size > 0 ? `?${query.toString()}` : '';
    return json<TranscriptsResponse>(`/transcripts${suffix}`);
  },
  transcript: (id: number) => json<TranscriptResponse>(`/transcripts/${id}`),
  addTranscript: (body: AddTranscriptRequest) => postJson('/transcripts', body),
  deleteTranscript: (id: number) => request(`/transcripts/${id}`, { method: 'DELETE' }),
  recordings: () => json<RecordingsResponse>('/transcripts/recordings'),
  transcribeRecording: (id: string, body: TranscribeRecordingRequest) =>
    postJson(`/transcripts/recordings/${encodeURIComponent(id)}/transcribe`, body),
  transcriptionJobs: () => json<TranscriptionJobsResponse>('/transcripts/jobs'),

  documents: (
    params: { kind?: HealthDocumentKind; tag?: string; query?: string; limit?: number } = {},
  ) => {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== '') query.set(key, String(value));
    }
    const suffix = query.size > 0 ? `?${query.toString()}` : '';
    return json<DocumentsResponse>(`/documents${suffix}`);
  },
  document: (id: number) => json<DocumentResponse>(`/documents/${id}`),
  addDocument: (body: AddDocumentRequest) =>
    postJson('/documents', body).then((r) => r.json() as Promise<DocumentResponse>),
  deleteDocument: (id: number) => request(`/documents/${id}`, { method: 'DELETE' }),
  attachDocumentFile: (id: number, file: File) => {
    const form = new FormData();
    form.append('file', file, file.name);
    return request(`/documents/${id}/file`, { method: 'POST', body: form });
  },
  detachDocumentFile: (id: number) => request(`/documents/${id}/file`, { method: 'DELETE' }),
  /** Same-origin link to the original file (the session cookie signs the request). */
  documentFileUrl: (id: number) => `/api/documents/${id}/file`,

  apartments: () => json<ApartmentsResponse>('/apartments'),
  utilityBills: (slug: string) =>
    json<UtilityBillsResponse>(`/apartments/${encodeURIComponent(slug)}/bills`),
  deleteUtilityBill: (id: number) =>
    request(`/apartment-utility-bills/${id}`, { method: 'DELETE' }),
  addApartment: (body: AddApartmentRequest) => postJson('/apartments', body),
  deleteApartment: (slug: string) =>
    request(`/apartments/${encodeURIComponent(slug)}`, { method: 'DELETE' }),
  unpaidApartmentPayments: () =>
    json<ApartmentPaymentsResponse>('/apartment-payments?unpaid=1&limit=50'),
  updateApartmentContact: (id: number, body: Partial<AddApartmentContactRequest>) =>
    patchJson(`/apartment-contacts/${id}`, body),
  updateApartmentPayment: (
    id: number,
    body: Partial<Omit<AddApartmentPaymentRequest, 'paidDate'>> & { paidDate?: string | null },
  ) => patchJson(`/apartment-payments/${id}`, body),
  updateApartmentDocument: (id: number, body: Partial<AddApartmentDocumentRequest>) =>
    patchJson(`/apartment-documents/${id}`, body),
  apartment: (slug: string) => json<ApartmentResponse>(`/apartments/${encodeURIComponent(slug)}`),
  updateApartment: (slug: string, body: UpdateApartmentRequest) =>
    patchJson(`/apartments/${encodeURIComponent(slug)}`, body),
  addApartmentContact: (slug: string, body: AddApartmentContactRequest) =>
    postJson(`/apartments/${encodeURIComponent(slug)}/contacts`, body),
  deleteApartmentContact: (id: number) =>
    request(`/apartment-contacts/${id}`, { method: 'DELETE' }),
  addApartmentPayment: (slug: string, body: AddApartmentPaymentRequest) =>
    postJson(`/apartments/${encodeURIComponent(slug)}/payments`, body),
  setApartmentPaymentPaid: (id: number, paidDate: string | null) =>
    patchJson(`/apartment-payments/${id}`, { paidDate }),
  deleteApartmentPayment: (id: number) =>
    request(`/apartment-payments/${id}`, { method: 'DELETE' }),
  apartmentDocument: (id: number) => json<ApartmentDocumentResponse>(`/apartment-documents/${id}`),
  addApartmentDocument: (slug: string, body: AddApartmentDocumentRequest) =>
    postJson(`/apartments/${encodeURIComponent(slug)}/documents`, body).then(
      (r) => r.json() as Promise<ApartmentDocumentResponse>,
    ),
  deleteApartmentDocument: (id: number) =>
    request(`/apartment-documents/${id}`, { method: 'DELETE' }),
  attachApartmentDocumentFile: (id: number, file: File) => {
    const form = new FormData();
    form.append('file', file, file.name);
    return request(`/apartment-documents/${id}/file`, { method: 'POST', body: form });
  },
  /** Same-origin link to the original file (the session cookie signs the request). */
  apartmentDocumentFileUrl: (id: number) => `/api/apartment-documents/${id}/file`,

  users: () => json<UsersResponse>('/users'),
  setGrant: (username: string, body: SetGrantRequest) =>
    json<UserDto>(`/users/${encodeURIComponent(username)}/grants`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  setRole: (username: string, body: SetRoleRequest) =>
    json<UserDto>(`/users/${encodeURIComponent(username)}/role`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  addUser: (body: AddUserRequest) =>
    postJson('/users', body).then((r) => r.json() as Promise<UserDto>),
  setDisabled: (username: string, body: SetDisabledRequest) =>
    json<UserDto>(`/users/${encodeURIComponent(username)}/disabled`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  removeUser: (username: string) =>
    json<{ removed: string }>(`/users/${encodeURIComponent(username)}`, { method: 'DELETE' }),

  schedule: () => json<ScheduleResponse>('/schedule'),
  scheduleSave: (settings: DigestSettings) =>
    json<DigestSettings>('/schedule/settings', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(settings),
    }),
  schedulePreview: () => json<SchedulePreviewResponse>('/schedule/preview'),
  scheduleSend: () =>
    json<{ sent: number; date: string; text: string }>('/schedule/send', { method: 'POST' }),
  telegramAccounts: () => json<TelegramAccountsResponse>('/telegram/accounts'),
  telegramLoginStart: (body: TelegramLoginStart) =>
    postJson('/telegram/login', body).then((r) => r.json() as Promise<TelegramLoginResponse>),
  telegramLoginCode: (login: string, code: string) =>
    postJson(`/telegram/login/${encodeURIComponent(login)}/code`, { code }).then(
      (r) => r.json() as Promise<TelegramLoginResponse>,
    ),
  telegramLoginPassword: (login: string, password: string) =>
    postJson(`/telegram/login/${encodeURIComponent(login)}/password`, { password }).then(
      (r) => r.json() as Promise<TelegramLoginResponse>,
    ),
  telegramLoginCancel: (login: string) =>
    request(`/telegram/login/${encodeURIComponent(login)}`, { method: 'DELETE' }),
  telegramAccountUpdate: (id: string, patch: TelegramAccountPatch) =>
    json<TelegramAccountView>(`/telegram/accounts/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    }),
  telegramAccountSignOut: (id: string) =>
    json<TelegramAccountView>(`/telegram/accounts/${encodeURIComponent(id)}/sign-out`, {
      method: 'POST',
    }),
  telegramAccountRemove: (id: string) =>
    request(`/telegram/accounts/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  telegramChats: (account: string, provider: ArchiveProvider = 'telegram') =>
    json<TelegramChatsResponse>(
      `/telegram/chats?account=${encodeURIComponent(account)}&provider=${provider}`,
    ),
  telegramGroups: (account: string, provider: ArchiveProvider = 'telegram') =>
    json<TelegramGroupsResponse>(`/${provider}/accounts/${encodeURIComponent(account)}/groups`),
  telegramChatAdd: (body: TelegramChatAdd) =>
    postJson('/telegram/chats', body).then((r) => r.json() as Promise<TelegramChatView>),
  telegramChatUpdate: (id: number, patch: TelegramChatPatch) =>
    json<TelegramChatView>(`/telegram/chats/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    }),
  emailSettings: () => json<EmailSettingsResponse>('/email/settings'),
  emailSettingsSave: (body: SmtpSettingsInput) =>
    json<EmailSettingsResponse>('/email/settings', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  emailSettingsReset: () => json<EmailSettingsResponse>('/email/settings', { method: 'DELETE' }),
  emailCheck: () => json<EmailCheckResponse>('/email/check', { method: 'POST' }),
  whatsappAccounts: () => json<WhatsAppAccountsResponse>('/whatsapp/accounts'),
  whatsappLinkStart: (body: WhatsAppLinkStart) =>
    postJson('/whatsapp/link', body).then((r) => r.json() as Promise<WhatsAppLinkResponse>),
  whatsappLinkStatus: (login: string) =>
    json<WhatsAppLinkResponse>(`/whatsapp/link/${encodeURIComponent(login)}`),
  whatsappLinkCancel: (login: string) =>
    request(`/whatsapp/link/${encodeURIComponent(login)}`, { method: 'DELETE' }),
  whatsappAccountUpdate: (id: string, patch: WhatsAppAccountPatch) =>
    json<WhatsAppAccountView>(`/whatsapp/accounts/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    }),
  whatsappAccountUnlink: (id: string) =>
    json<WhatsAppAccountView>(`/whatsapp/accounts/${encodeURIComponent(id)}/unlink`, {
      method: 'POST',
    }),
  whatsappAccountRemove: (id: string) =>
    request(`/whatsapp/accounts/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  createSession: () => json<CreateSessionResponse>('/chat/sessions', { method: 'POST' }),
  closeSession: (id: string) => request(`/chat/sessions/${id}`, { method: 'DELETE' }),
  sendMessage: (id: string, text: string) => postJson(`/chat/sessions/${id}/messages`, { text }),
  resolveApproval: (id: string, requestId: string, allow: boolean) =>
    postJson(`/chat/sessions/${id}/approvals/${requestId}`, { allow }),
  interrupt: (id: string) => request(`/chat/sessions/${id}/interrupt`, { method: 'POST' }),
  events: (id: string, after: number, signal: AbortSignal) =>
    request(`/chat/sessions/${id}/events?after=${after}`, {
      headers: { Accept: 'text/event-stream' },
      signal,
    }),
};
